import {
  IonContent,
  IonPage,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonBackButton,
  IonText,
  IonSpinner,
  IonIcon,
  IonAlert,
  IonButton,
  IonRefresher,
  IonRefresherContent,
  IonSelect,
  IonSelectOption,
  useIonToast,
  useIonViewDidEnter,
  useIonViewDidLeave,
} from '@ionic/react';
import type React from 'react';
import { closeCircle, personOutline, timeOutline, fileTrayFullOutline, calendarOutline, sendOutline, informationCircleOutline, personRemoveOutline, walletOutline } from 'ionicons/icons';
import { useQuery, useMutation } from '@apollo/client/react';
import { GET_ACTIVE_MARKET_ENTRIES } from '../../../graphql/queries/activeMarketEntries';
import { GET_MY_MARKET_BIDS } from '../../../graphql/queries/myMarketBids';
import { GET_CURRENT_FANTASY_LEAGUE_MEMBER } from '../../../graphql/queries/currentFantasyLeagueMember';
import { GET_VISIBLE_TRANSFER_OFFERS } from '../../../graphql/queries/visibleTransferOffers';
import type {
  GetActiveMarketEntriesQuery,
  GetActiveMarketEntriesQueryVariables,
  GetMyMarketBidsQuery,
  GetMyMarketBidsQueryVariables,
  GetCurrentFantasyLeagueMemberQuery,
  GetCurrentFantasyLeagueMemberQueryVariables,
  GetVisibleTransferOffersQuery,
  GetVisibleTransferOffersQueryVariables,
  WithdrawBidMutation,
  WithdrawBidMutationVariables,
} from '../../../types/graphql-generated';
import { useNavigate } from 'react-router-dom';
import { PlayerCard } from '../components/PlayerCard';
import { PlaceBidModal, OfferTargetFactory } from '../components/PlaceBidModal';
import { MarketTimeCounter } from './components/MarketTimeCounter';
import { useRef, useState } from 'react';
import { WITHDRAW_BID } from '../../../graphql/mutations/marketEntry';
import { useRemovePlayerFromMarket } from '../hooks/useRemovePlayerFromMarket';
import type { RefresherCustomEvent } from '@ionic/react';
import { HelpModal } from '../components/HelpModal';
import { getMarketTabHelp } from '../../../constants/helpContent';
import { AppSectionHeader } from '../../components/ui/AppSectionHeader';
import { LeagueSettingsButton } from '../components/LeagueSettingsButton';
import { SelectField } from '../../../components/ui/SelectField';
import { ContentSectionHeader } from '@/components/ui/ContentSectionHeader';

interface MarketTabProps {
  leagueId: string;
}

type SellerFilter = 'all' | 'futyclub' | 'self' | 'others';

const MARKET_POLL_INTERVAL_MS = 60_000;

const formatRemainingTime = (expiresAt: string): string => {
  const now = new Date();
  const expiry = new Date(expiresAt);
  const diff = expiry.getTime() - now.getTime();

  if (diff <= 0) {
    return 'Expirado';
  }

  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

  if (hours >= 24) {
    const days = Math.floor(hours / 24);
    return `${days}d ${hours % 24}h`;
  }

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
};

export const MarketTab = ({ leagueId }: MarketTabProps) => {
  const navigate = useNavigate();
  const [presentToast] = useIonToast();
  const { requestRemoveFromMarket, removeFromMarketAlert } = useRemovePlayerFromMarket(leagueId);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<typeof marketEntries[0] | null>(null);
  const [withdrawAlertOpen, setWithdrawAlertOpen] = useState(false);
  const [marketEntryToWithdraw, setMarketEntryToWithdraw] = useState<string | null>(null);
  const [sellerFilter, setSellerFilter] = useState<SellerFilter>('all');
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
  const sellerSelectRef = useRef<HTMLIonSelectElement>(null);

  const {
    data: marketData,
    loading: marketLoading,
    error: marketError,
    refetch: refetchMarket,
    startPolling: startMarketPolling,
    stopPolling: stopMarketPolling,
  } = useQuery<
    GetActiveMarketEntriesQuery,
    GetActiveMarketEntriesQueryVariables
  >(GET_ACTIVE_MARKET_ENTRIES, {
    variables: { fantasyLeagueId: leagueId },
    pollInterval: MARKET_POLL_INTERVAL_MS,
    skipPollAttempt: () => document.hidden,
  });

  const {
    data: bidsData,
    loading: bidsLoading,
    error: bidsError,
    refetch: refetchBids,
    startPolling: startBidsPolling,
    stopPolling: stopBidsPolling,
  } = useQuery<
    GetMyMarketBidsQuery,
    GetMyMarketBidsQueryVariables
  >(GET_MY_MARKET_BIDS, {
    variables: { fantasyLeagueId: leagueId },
    pollInterval: MARKET_POLL_INTERVAL_MS,
    skipPollAttempt: () => document.hidden,
  });

  const {
    data: memberData,
    loading: memberLoading,
    error: memberError,
    refetch: refetchMember,
    startPolling: startMemberPolling,
    stopPolling: stopMemberPolling,
  } = useQuery<
    GetCurrentFantasyLeagueMemberQuery,
    GetCurrentFantasyLeagueMemberQueryVariables
  >(GET_CURRENT_FANTASY_LEAGUE_MEMBER, {
    variables: { fantasyLeagueId: leagueId },
    pollInterval: MARKET_POLL_INTERVAL_MS,
    skipPollAttempt: () => document.hidden,
  });

  const {
    data: transferOffersData,
    loading: transferOffersLoading,
    error: transferOffersError,
    refetch: refetchTransferOffers,
    startPolling: startTransferOffersPolling,
    stopPolling: stopTransferOffersPolling,
  } = useQuery<
    GetVisibleTransferOffersQuery,
    GetVisibleTransferOffersQueryVariables
  >(GET_VISIBLE_TRANSFER_OFFERS, {
    variables: { fantasyLeagueId: leagueId },
    pollInterval: MARKET_POLL_INTERVAL_MS,
    skipPollAttempt: () => document.hidden,
  });

  useIonViewDidEnter(() => {
    startMarketPolling(MARKET_POLL_INTERVAL_MS);
    startBidsPolling(MARKET_POLL_INTERVAL_MS);
    startMemberPolling(MARKET_POLL_INTERVAL_MS);
    startTransferOffersPolling(MARKET_POLL_INTERVAL_MS);
  }, [startMarketPolling, startBidsPolling, startMemberPolling, startTransferOffersPolling]);

  useIonViewDidLeave(() => {
    stopMarketPolling();
    stopBidsPolling();
    stopMemberPolling();
    stopTransferOffersPolling();
  }, [stopMarketPolling, stopBidsPolling, stopMemberPolling, stopTransferOffersPolling]);

  const [withdrawBid] = useMutation<
    WithdrawBidMutation,
    WithdrawBidMutationVariables
  >(WITHDRAW_BID, {
    refetchQueries: [
      {
        query: GET_MY_MARKET_BIDS,
        variables: { fantasyLeagueId: leagueId },
      },
    ],
  });

  const onRefresh = async (ev: RefresherCustomEvent) => {
    try {
      await Promise.all([
        refetchMarket({ fantasyLeagueId: leagueId }),
        refetchBids({ fantasyLeagueId: leagueId }),
        refetchMember({ fantasyLeagueId: leagueId }),
        refetchTransferOffers({ fantasyLeagueId: leagueId }),
      ]);
    } finally {
      ev.detail.complete();
    }
  };

  // Polls and manual refetches keep their previous data. Only block the market
  // before every required query has produced its first result.
  const initialLoading =
    (marketLoading && !marketData) ||
    (bidsLoading && !bidsData) ||
    (memberLoading && !memberData) ||
    (transferOffersLoading && !transferOffersData);
  const initialError =
    (!marketData ? marketError : undefined) ||
    (!bidsData ? bidsError : undefined) ||
    (!memberData ? memberError : undefined) ||
    (!transferOffersData ? transferOffersError : undefined);
  const marketEntries = marketData?.activeMarketEntries || [];
  const visibleBids = bidsData?.visibleBids || [];
  const visibleTransferOffers = transferOffersData?.visibleTransferOffers || [];

  // Find current user's membership
  const currentUserMember = memberData?.currentFantasyLeagueMember;

  const isFutyclubEntry = (entry: typeof marketEntries[0]) => !entry.seller?.id;
  const isMyEntry = (entry: typeof marketEntries[0]) =>
    Boolean(currentUserMember?.id) && entry.seller?.id === currentUserMember?.id;
  const isOtherEntry = (entry: typeof marketEntries[0]) =>
    Boolean(entry.seller?.id) && !isMyEntry(entry);

  const filteredMarketEntries = marketEntries.filter((entry) => {
    switch (sellerFilter) {
      case 'futyclub':
        return isFutyclubEntry(entry);
      case 'self':
        return isMyEntry(entry);
      case 'others':
        return isOtherEntry(entry);
      case 'all':
      default:
        return true;
    }
  });

  // Default order: Futyclub (free agency) entries first, then league members'
  // entries. Within each group, players that expire sooner are shown first.
  const sortedMarketEntries = [...filteredMarketEntries].sort((a, b) => {
    const groupA = isFutyclubEntry(a) ? 0 : 1;
    const groupB = isFutyclubEntry(b) ? 0 : 1;
    if (groupA !== groupB) {
      return groupA - groupB;
    }

    const expiryA = a.expiresAt ? new Date(a.expiresAt).getTime() : Infinity;
    const expiryB = b.expiresAt ? new Date(b.expiresAt).getTime() : Infinity;
    return expiryA - expiryB;
  });

  const SELLER_FILTER_OPTIONS: Array<{ value: SellerFilter; label: string }> = [
    { value: 'all', label: 'Todos' },
    { value: 'futyclub', label: 'Futyclub' },
    { value: 'self', label: 'Yo' },
    { value: 'others', label: 'Otros' },
  ];

  const sellerFilterLabel =
    SELLER_FILTER_OPTIONS.find((o) => o.value === sellerFilter)?.label ?? 'Todos';

  const handleEntryClick = (entry: typeof marketEntries[0]) => {
    // Don't open modal if current user is the seller
    if (entry.seller?.id === currentUserMember?.id) {
      return;
    }

    setSelectedEntry(entry);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedEntry(null);
  };

  const handleWithdrawBid = (marketEntryId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click handler from firing
    setMarketEntryToWithdraw(marketEntryId);
    setWithdrawAlertOpen(true);
  };

  const confirmWithdrawBid = async () => {
    if (!marketEntryToWithdraw) return;

    try {
      const result = await withdrawBid({
        variables: { marketEntryId: marketEntryToWithdraw },
      });

      const errors = result.data?.withdrawBid?.errors ?? [];
      if (errors.length > 0) {
        await presentToast({
          message: errors.join(', '),
          duration: 3000,
          color: 'danger',
        });
      }
    } catch (error) {
      console.error('Error withdrawing bid:', error);
      await presentToast({
        message: 'Error al retirar la oferta',
        duration: 3000,
        color: 'danger',
      });
    } finally {
      setWithdrawAlertOpen(false);
      setMarketEntryToWithdraw(null);
    }
  };

  // Find existing bid for a market entry
  const getExistingBid = (marketEntryId: string) => {
    return visibleBids.find(
      (bid) => bid.marketEntry.id === marketEntryId && bid.bidder?.id === currentUserMember?.id
    );
  };

  const myOpenBids =
    currentUserMember
      ? visibleBids.filter((bid) => bid.bidder?.id === currentUserMember.id)
      : [];

  // Count received bids: bids on my market entries from other users
  const receivedBidsCount = currentUserMember
    ? visibleBids.filter((bid) => {
        const marketEntry = marketEntries.find((entry) => entry.id === bid.marketEntry.id);
        return marketEntry?.seller?.id === currentUserMember.id && bid.bidder?.id !== currentUserMember.id;
      }).length
    : 0;

  const receivedTransferOffersCount = currentUserMember
    ? visibleTransferOffers.filter((offer) => offer.seller?.id === currentUserMember.id).length
    : 0;

  const receivedOffersCount = receivedBidsCount + receivedTransferOffersCount;

  const handleNavigateToRecibidas = () => {
    navigate(`/app/ligas/${leagueId}/mercado/recibidas`);
  };

  const handleNavigateToPasadas = () => {
    navigate(`/app/ligas/${leagueId}/mercado/pasadas`);
  };

  const handleNavigateToMember = (
    fantasyLeagueMemberId: string,
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.stopPropagation();
    navigate(`/app/ligas/${leagueId}/mercado/plantilla/${fantasyLeagueMemberId}`);
  };

  const getWithdrawAlertTexts = () => {
    return {
      header: 'Retirar oferta',
      message: '¿Estás seguro de que quieres retirar tu oferta?',
    };
  };

  return (
    <IonPage>
      <AppSectionHeader variant="fantasy">
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/app/main/mis-ligas" />
          </IonButtons>
          <IonTitle className="ml-4">Fichajes</IonTitle>
          <IonButtons slot="end">
            <IonButton aria-label="Ayuda" onClick={() => setIsHelpModalOpen(true)}>
              <IonIcon slot="icon-only" icon={informationCircleOutline} />
            </IonButton>
            <LeagueSettingsButton leagueId={leagueId} />
          </IonButtons>
        </IonToolbar>
      </AppSectionHeader>
      <IonContent className="ion-padding">
        <IonRefresher slot="fixed" onIonRefresh={onRefresh}>
          <IonRefresherContent />
        </IonRefresher>
        <MarketTimeCounter />

        {currentUserMember && (
          <div className="mb-4 mt-3 flex flex-col gap-2.5">
            {/* Budget card */}
            <div className="flex items-center gap-3 rounded-2xl border border-[#e8ebf1] bg-white px-4 py-3 shadow-[0_1px_2px_rgba(20,30,60,0.04)]">
              <span
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[13px] text-[#f3e2ac]"
                style={{ background: 'var(--fc-green-gradient)' }}
              >
                <IonIcon icon={walletOutline} className="text-xl" aria-label="Presupuesto" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-[10.5px] font-extrabold uppercase tracking-wide text-[var(--fc-ink-3)]">
                  Saldo disponible
                </div>
                <div className="font-heading text-[21px] font-extrabold leading-tight">
                  {currentUserMember.budget.toLocaleString('es-ES')} €
                </div>
              </div>
              {myOpenBids.length > 0 && (
                <div className="shrink-0 text-right">
                  <div className="text-[10.5px] font-extrabold uppercase tracking-wide text-[var(--fc-ink-3)]">
                    Tras {myOpenBids.length} {myOpenBids.length === 1 ? 'oferta' : 'ofertas'}
                  </div>
                  <div
                    className={`font-heading text-[15px] font-bold ${
                      currentUserMember.budget - myOpenBids.reduce((sum, bid) => sum + bid.amount, 0) < 0
                        ? 'text-red-600'
                        : 'text-[var(--fc-ink)]'
                    }`}
                  >
                    {(currentUserMember.budget - myOpenBids.reduce((sum, bid) => sum + bid.amount, 0)).toLocaleString('es-ES')} €
                  </div>
                </div>
              )}
            </div>

            {/* Navigation buttons */}
            <div className="grid grid-cols-2 gap-2.5">
              <button
                type="button"
                onClick={handleNavigateToRecibidas}
                className="flex min-h-[52px] items-center justify-center gap-1.5 rounded-2xl border border-[#e2e7ef] bg-white px-3 py-3.5 text-center text-[12.5px] font-bold leading-tight text-[var(--fc-navy)] shadow-[0_1px_2px_rgba(20,30,60,0.04)] transition-colors hover:bg-slate-50"
              >
                <IonIcon icon={fileTrayFullOutline} className="text-lg text-[var(--fc-green)]" />
                <span>Ofertas recibidas</span>
                {receivedOffersCount > 0 && (
                  <span
                    data-testid="received-offers-badge"
                    className="ml-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-red-600 px-1 text-[11px] font-extrabold leading-none text-white"
                  >
                    {receivedOffersCount}
                  </span>
                )}
              </button>
              <button
                type="button"
                onClick={handleNavigateToPasadas}
                className="flex min-h-[52px] items-center justify-center gap-1.5 rounded-2xl border border-[#e2e7ef] bg-white px-3 py-3.5 text-center text-[12.5px] font-bold leading-tight text-[var(--fc-navy)] shadow-[0_1px_2px_rgba(20,30,60,0.04)] transition-colors hover:bg-slate-50"
              >
                <IonIcon icon={calendarOutline} className="text-lg text-[var(--fc-green)]" />
                <span>Historial de ofertas</span>
              </button>
            </div>
          </div>
        )}

        <div className="relative mb-3">
          <SelectField
            label="En venta por"
            value={sellerFilterLabel}
            aria-label="Filtrar por vendedor"
            onClick={() => sellerSelectRef.current?.open()}
          />
          <IonSelect
            ref={sellerSelectRef}
            aria-label="En venta por"
            interface="popover"
            value={sellerFilter}
            onIonChange={(event) => setSellerFilter(event.detail.value as SellerFilter)}
            className="pointer-events-none absolute inset-0 opacity-0"
          >
            {SELLER_FILTER_OPTIONS.map((opt) => (
              <IonSelectOption key={opt.value} value={opt.value}>
                {opt.label}
              </IonSelectOption>
            ))}
          </IonSelect>
        </div>

        {initialLoading && (
          <div className="flex justify-center items-center min-h-[200px]">
            <IonSpinner name="crescent" />
          </div>
        )}

        {initialError && (
          <IonText color="danger">
            <p>Error al cargar el mercado</p>
          </IonText>
        )}

        {!initialLoading && !initialError && sortedMarketEntries.length === 0 && (
          <IonText>
            <div className="text-center mt-8">
              <h2 className="text-xl font-semibold">No hay jugadores en venta</h2>
              <p className="text-gray-500 mt-2">
                No hay resultados para este filtro
              </p>
            </div>
          </IonText>
        )}

        {!initialLoading && !initialError && sortedMarketEntries.length > 0 && (
          <div>
            <ContentSectionHeader
              label="Jugadores disponibles"
              count={`${sortedMarketEntries.length} en venta`}
              className="mb-3"
            />

            {sortedMarketEntries.map((entry) => {
              const existingBid = getExistingBid(entry.id);
              const isOwnPlayer = isMyEntry(entry);

              // Highlight own players (light blue) and other league members'
              // players (light green). Futyclub entries keep the default style.
              const cardClass = isOwnPlayer
                ? 'border-l-4 border-blue-300 bg-blue-50/60'
                : isOtherEntry(entry)
                  ? 'border-l-4 border-green-300 bg-green-50/60'
                  : '';

              return (
                <div key={entry.id} className="mb-3">
                  <PlayerCard
                    key={entry.player.id}
                    player={entry.player}
                    className={cardClass}
                    pvp={entry.price}
                    footerDetails={
                      <div className="flex min-w-0 flex-col gap-1">
                        {entry.seller?.id ? (
                          <button
                            type="button"
                            onClick={(event) => handleNavigateToMember(entry.seller!.id, event)}
                            aria-label={`Ver plantilla de ${entry.seller.teamName || 'Usuario'}`}
                            className="flex min-w-0 items-center gap-1.5 font-semibold text-slate-600 transition-colors hover:text-[var(--fc-green)]"
                          >
                            <IonIcon icon={personOutline} className="shrink-0 text-sm" />
                            <span className="truncate">{entry.seller.teamName || 'Usuario'}</span>
                          </button>
                        ) : (
                          <div className="flex items-center gap-1.5 font-semibold text-slate-600">
                            <IonIcon icon={personOutline} className="shrink-0 text-sm" />
                            <span className="truncate">Futyclub</span>
                          </div>
                        )}

                        <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs font-semibold text-slate-400">
                          {entry.bidCount > 0 && (
                            <span>
                              {entry.bidCount} {entry.bidCount === 1 ? 'interesado' : 'interesados'}
                            </span>
                          )}
                          {entry.bidCount > 0 && entry.expiresAt && <span aria-hidden="true">·</span>}
                          {entry.expiresAt && (
                            <span className="inline-flex items-center gap-1">
                              <IonIcon icon={timeOutline} className="text-xs" />
                              {formatRemainingTime(entry.expiresAt)}
                            </span>
                          )}
                          {existingBid && (
                            <span className="ml-0.5 rounded-full bg-blue-100 px-2.5 py-1 text-xs font-bold text-blue-900">
                              Oferta: {existingBid.amount.toLocaleString('es-ES')}€
                            </span>
                          )}
                        </div>
                      </div>
                    }
                    footerActions={
                      isOwnPlayer
                        ? [
                            {
                              label: 'Retirar del mercado',
                              icon: personRemoveOutline,
                              onClick: () => requestRemoveFromMarket(entry.id),
                              variant: 'danger',
                            },
                          ]
                        : [
                            {
                              label: existingBid ? 'Modificar' : 'Fichar',
                              icon: sendOutline,
                              onClick: () => handleEntryClick(entry),
                            },
                            ...(existingBid
                              ? [{
                                  label: 'Retirar oferta',
                                  icon: closeCircle,
                                  onClick: (event: React.MouseEvent<HTMLButtonElement>) => handleWithdrawBid(entry.id, event),
                                  variant: 'danger' as const,
                                }]
                              : []),
                          ]
                    }
                  />
                </div>
              );
            })}
          </div>
        )}

        {selectedEntry && (
          <PlaceBidModal
            isOpen={isModalOpen}
            target={OfferTargetFactory.fromMarketEntry(selectedEntry, getExistingBid(selectedEntry.id) || null)}
            leagueId={leagueId}
            onClose={handleCloseModal}
          />
        )}

        <IonAlert
          isOpen={withdrawAlertOpen}
          header={getWithdrawAlertTexts().header}
          message={getWithdrawAlertTexts().message}
          buttons={[
            {
              text: 'Mantener',
              role: 'cancel',
              handler: () => {
                setWithdrawAlertOpen(false);
                setMarketEntryToWithdraw(null);
              },
            },
            {
              text: 'Retirar',
              role: 'destructive',
              handler: confirmWithdrawBid,
            },
          ]}
          onDidDismiss={() => {
            setWithdrawAlertOpen(false);
            setMarketEntryToWithdraw(null);
          }}
        />

        {removeFromMarketAlert}

      </IonContent>

      <HelpModal
        isOpen={isHelpModalOpen}
        onClose={() => setIsHelpModalOpen(false)}
        title={getMarketTabHelp().title}
        content={getMarketTabHelp().content}
      />
    </IonPage>
  );
};
